import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, fastChannelApi } from '@/api/client';

/**
 * FastChannel (FAST playout) API.
 *
 * The control plane (channels/schedules/storages) is served by
 * **@parrot/@controlplane** (`fastChannelApi` → flat `/api/v1` routes; tenant is
 * resolved from the JWT, so there is no `/services/{id}` path segment). The
 * service list still comes from the v3 service catalog (`serviceApiV3 /services`),
 * filtered to the FastChannel provider scope.
 *
 * Controlplane responses are RAW JSON (no `{data}` envelope), fields are
 * snake_case, and channel status is `active|inactive`.
 */

// ============================================
// Types
// ============================================

export const FASTCHANNEL_SCOPE = 'Internal.FastChannel';

/** A provisioned FastChannel service (one row from the v3 `/services` catalog). */
export interface FastChannelService {
  id: string;
  service_name: string;
  provider_scope: string;
  provider_name?: string;
  status: string;
  zones_count?: number;
  created_at?: string;
}

export type ChannelStatus = 'active' | 'inactive';

export interface Channel {
  id: string;
  name: string;
  title: string;
  description?: string;
  tenant?: string;
  repeat?: boolean;
  repeat_mode?: string;
  status?: ChannelStatus | string;
}

export type ScheduleType = 'vod' | 'live' | string;

export interface Schedule {
  id: string;
  channel?: string;
  title: string;
  description?: string;
  duration: number;
  /** Epoch milliseconds (derived from start_time/end_time or start_at/end_at). */
  start_time: number;
  end_time: number;
  url?: string;
  live_url?: string;
  type: ScheduleType;
}

export interface Storage {
  id: string;
  name: string;
  title: string;
  tenant?: string;
  type?: string;
  /** Public base used to build a file's playable source URL. */
  public_url?: string;
}

/** Create/update storage payload. The S3 credential fields are write-only —
 *  the controlplane encrypts them server-side and never returns them. */
export interface StorageInput {
  name: string;
  title?: string;
  type?: string;
  public_url?: string;
  bucket?: string;
  access_key?: string;
  secret?: string;
  endpoint?: string;
  region?: string;
}

export interface StorageFile {
  key: string;
  etag?: string;
  last_modified?: string;
  size?: number;
  storage_class?: string;
}

/**
 * A media asset in the Content Library. Raw controlplane JSON (snake_case): a
 * catalog entry that maps a storage object to a named, playable source with
 * resolved duration/size. `url` is the playable source (already built by the
 * controlplane), so the schedule "Pick from Library" path can use it directly
 * without a duration probe.
 */
export interface Asset {
  id: string;
  storage_id: string;
  object_key: string;
  name: string;
  /** Playout-ready URL. EMPTY until normalization completes (status `ready`):
   *  the control plane normalizes a source to H.264 + AAC stereo before exposing
   *  a playable url, so consumers must gate on `status === 'ready'`. */
  url: string;
  /** Original registered source URL (input to normalization). */
  source_url: string;
  duration_seconds: number;
  size_bytes: number;
  content_type: string;
  /** pending | normalizing | ready | failed */
  status: string;
  /** Sub-step while status is `normalizing`: downloading | encoding | uploading. */
  stage?: string;
  /** 0-100, meaningful during `encoding`. */
  progress?: number;
  /** Probed source codecs (diagnostics / UI labels). */
  video_codec: string;
  audio_codec: string;
  /** Failure reason — present only when status is `failed`. */
  error?: string;
  /** Per-stage normalization metadata (timings, codecs, sizes); present once the
   *  worker writes the first stage. */
  normalize_meta?: NormalizeMeta;
  created_at: string;
  updated_at: string;
}

/** Per-stage normalization metadata (controlplane `normalize_meta` JSONB). Each
 *  key appears once its stage completes. */
export interface NormalizeMeta {
  download?: { ms: number; bytes: number };
  source?: {
    video_codec: string;
    audio_codec?: string;
    audio_channels?: number;
    width?: number;
    height?: number;
    video_duration?: number;
    audio_duration?: number;
  };
  encode?: {
    ms: number;
    video_codec: string;
    audio_codec: string;
    segment_seconds: number;
    segments: number;
  };
  upload?: { ms: number; files: number; bytes: number };
}

/** Create-asset payload (controlplane raw body). */
export interface CreateAssetInput {
  storage_id: string;
  object_key: string;
  name?: string;
  /** Optional client-known hints (upload: from the File; storage map: from the
   *  listing). The control plane stores them as-is — it does not HeadObject. */
  size_bytes?: number;
  content_type?: string;
}

/** Presigned upload descriptor returned by `POST /storages/{id}/upload-url`.
 *  The browser PUTs the file directly to `url` (the storage), NOT the API. */
export interface UploadUrlInput {
  storageId: string;
  object_key: string;
  content_type: string;
}

export interface UploadUrl {
  url: string;
  method: string;
  headers: Record<string, string>;
  expires_in_seconds: number;
}

export interface StorageFilesResult {
  common_prefixes: { prefix: string }[];
  contents: StorageFile[];
  is_truncated?: boolean;
  marker?: string;
  next_marker?: string;
}

/** Create-channel payload (controlplane raw body). */
export interface CreateChannelInput {
  name: string;
  title: string;
  description?: string;
  repeat?: boolean;
  repeat_mode?: string;
  status?: ChannelStatus;
}

export interface UpdateChannelInput {
  title?: string;
  description?: string;
  repeat?: boolean;
  repeat_mode?: string;
  status?: ChannelStatus;
}

/** Create-schedule payload (controlplane raw body; epoch-ms times). */
export interface CreateScheduleInput {
  channel: string;
  title: string;
  description?: string;
  type: ScheduleType;
  url: string;
  /** Epoch milliseconds. */
  start_time: number;
  end_time: number;
  duration: number;
  live_url?: string;
}

/** Envelope only for the v3 service catalog (still @mgmt-shaped). */
interface CatalogEnvelope<T> {
  status?: number;
  data: T;
  message?: string;
}

/** Raw schedule row as returned by controlplane (snake_case, nullable times). */
interface RawSchedule {
  id: string;
  channel?: string;
  title?: string;
  description?: string;
  start_time?: number | null;
  end_time?: number | null;
  start_at?: string | null;
  end_at?: string | null;
  url?: string;
  duration?: number | null;
  type?: ScheduleType;
  live_url?: string;
}

function toMs(ms?: number | null, iso?: string | null): number {
  if (typeof ms === 'number' && ms > 0) return ms;
  if (iso) {
    const t = Date.parse(iso);
    if (!Number.isNaN(t)) return t;
  }
  return 0;
}

/** Map a controlplane schedule row to the UI Schedule shape (epoch-ms times). */
function normalizeSchedule(raw: RawSchedule): Schedule {
  return {
    id: raw.id,
    channel: raw.channel,
    title: raw.title ?? '',
    description: raw.description,
    duration: raw.duration ?? 0,
    start_time: toMs(raw.start_time, raw.start_at),
    end_time: toMs(raw.end_time, raw.end_at),
    url: raw.url,
    live_url: raw.live_url,
    type: raw.type ?? 'vod',
  };
}

// ============================================
// Query keys
// ============================================

export const fastChannelKeys = {
  all: ['fastchannel'] as const,
  services: ['fastchannel', 'services'] as const,
  channels: (s: string) => ['fastchannel', 'channels', s] as const,
  channel: (s: string, c: string) => ['fastchannel', 'channels', s, c] as const,
  schedules: (s: string, c: string) => ['fastchannel', 'schedules', s, c] as const,
  storages: (s: string) => ['fastchannel', 'storages', s] as const,
  storageFiles: (s: string, st: string, path: string, marker: string) =>
    ['fastchannel', 'storages', s, st, path, marker] as const,
  assets: (s: string) => ['fastchannel', 'assets', s] as const,
  // Packager cache/PVC status is cluster-wide (tenant from JWT), not per-service.
  cache: ['fastchannel', 'cache'] as const,
  analytics: (c: string) => ['fastchannel', 'analytics', c] as const,
};

// ============================================
// Services (which FastChannel services does this customer have?)
// ============================================

interface RawService {
  id?: string;
  service_id?: string;
  service_name?: string;
  status?: string;
  zones_count?: number;
  created_at?: string;
  provider_id?: string;
  provider_name?: string;
  provider_scope?: string;
  provider?: { name?: string; display_name?: string; legacy_scope?: string; scope?: string };
}

function rawScope(s: RawService): string {
  return s.provider?.legacy_scope ?? s.provider?.scope ?? s.provider_scope ?? 'unknown';
}

function normalizeService(s: RawService): FastChannelService {
  return {
    id: String(s.id ?? s.service_id ?? ''),
    service_name: s.service_name ?? '',
    provider_scope: rawScope(s),
    provider_name: s.provider?.display_name ?? s.provider_name ?? s.provider?.name,
    status: s.status ?? 'unknown',
    zones_count: s.zones_count,
    created_at: s.created_at,
  };
}

/**
 * Identify a FastChannel service. Primary signal is the provider scope
 * `Internal.FastChannel`; we also fall back to the provider name (`parrot`) and
 * a fuzzy "fast channel" match so a service with a missing `legacy_scope` still
 * resolves.
 */
function isFastChannelService(s: RawService): boolean {
  if (rawScope(s) === FASTCHANNEL_SCOPE) return true;
  const name = (s.provider?.name ?? s.provider_name ?? '').toLowerCase();
  if (name === 'parrot') return true;
  const haystack = `${s.provider?.display_name ?? ''} ${s.provider_name ?? ''}`.toLowerCase();
  return /fast\s*channel/.test(haystack);
}

/**
 * The customer's provisioned FastChannel services (owned + shared), filtered
 * from the v3 service catalog by provider scope. The catalog is still served by
 * @mgmt v3 (enveloped), unlike the controlplane resources below.
 */
export function useFastChannelServices() {
  return useQuery<FastChannelService[]>({
    queryKey: fastChannelKeys.services,
    staleTime: 30_000,
    queryFn: async () => {
      const PER_PAGE = 50;
      const MAX_PAGES = 40;
      const all: RawService[] = [];
      let page = 1;
      let total = Infinity;
      while (all.length < total && page <= MAX_PAGES) {
        const res = await serviceApiV3.get<
          CatalogEnvelope<{ services?: RawService[]; total?: number }>
        >('/services', { params: { per_page: PER_PAGE, page } });
        const d = res.data?.data;
        const batch = d?.services ?? [];
        all.push(...batch);
        total = d?.total ?? all.length;
        if (batch.length < PER_PAGE) break;
        page += 1;
      }
      return all.filter(isFastChannelService).map(normalizeService);
    },
  });
}

// ============================================
// Channels  (controlplane: /channels, raw JSON, tenant from JWT)
// ============================================

export function useChannels(serviceUuid: string | undefined) {
  return useQuery<Channel[]>({
    queryKey: fastChannelKeys.channels(serviceUuid ?? ''),
    enabled: !!serviceUuid,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fastChannelApi.get<Channel[]>('/channels');
      return res.data ?? [];
    },
  });
}

export function useChannelDetail(
  serviceUuid: string | undefined,
  channelUuid: string | undefined,
) {
  return useQuery<Channel | null>({
    queryKey: fastChannelKeys.channel(serviceUuid ?? '', channelUuid ?? ''),
    enabled: !!serviceUuid && !!channelUuid,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fastChannelApi.get<Channel>(`/channels/${channelUuid}`);
      return res.data ?? null;
    },
  });
}

export function useCreateChannel(
  serviceUuid: string,
  options?: UseMutationOptions<Channel, unknown, CreateChannelInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateChannelInput) => {
      const res = await fastChannelApi.post<Channel>('/channels', input);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.channels(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useFlushChannel(
  _serviceUuid: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (channelUuid: string) => {
      const res = await fastChannelApi.delete(`/channels/${channelUuid}/flush`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateChannel(
  serviceUuid: string,
  channelUuid: string,
  options?: UseMutationOptions<Channel, unknown, UpdateChannelInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateChannelInput) => {
      const res = await fastChannelApi.patch<Channel>(`/channels/${channelUuid}`, input);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.channels(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Toggle a channel's status from a list view (channel id passed per call). */
export function useSetChannelStatus(
  serviceUuid: string,
  options?: UseMutationOptions<
    unknown,
    unknown,
    { channelUuid: string; status: ChannelStatus }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ channelUuid, status }: { channelUuid: string; status: ChannelStatus }) => {
      const res = await fastChannelApi.patch(`/channels/${channelUuid}`, { status });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.channels(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteChannel(
  serviceUuid: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (channelUuid: string) => {
      const res = await fastChannelApi.delete(`/channels/${channelUuid}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.channels(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Schedules  (controlplane: list under channel, flat create/patch/delete)
// ============================================

export function useSchedules(
  serviceUuid: string | undefined,
  channelUuid: string | undefined,
  paused?: boolean,
) {
  return useQuery<Schedule[]>({
    queryKey: fastChannelKeys.schedules(serviceUuid ?? '', channelUuid ?? ''),
    enabled: !!serviceUuid && !!channelUuid,
    staleTime: 15_000,
    refetchInterval: paused ? false : 15_000,
    queryFn: async () => {
      const res = await fastChannelApi.get<RawSchedule[]>(
        `/channels/${channelUuid}/schedules`,
      );
      return (res.data ?? []).map(normalizeSchedule);
    },
  });
}

export function useCreateSchedule(
  serviceUuid: string,
  channelUuid: string,
  options?: UseMutationOptions<unknown, unknown, CreateScheduleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateScheduleInput) => {
      const res = await fastChannelApi.post('/schedules', input);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.schedules(serviceUuid, channelUuid) });
      // Tell the control plane to resync the packager (best-effort).
      void resyncChannel(channelUuid).catch(() => {});
      options?.onSuccess?.(...args);
    },
  });
}

export interface UpdateScheduleInput {
  scheduleId: string;
  schedule: Partial<CreateScheduleInput>;
}

export function useUpdateSchedule(
  serviceUuid: string,
  channelUuid: string,
  options?: UseMutationOptions<unknown, unknown, UpdateScheduleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleId, schedule }: UpdateScheduleInput) => {
      const res = await fastChannelApi.patch(`/schedules/${scheduleId}`, schedule);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.schedules(serviceUuid, channelUuid) });
      // Tell the control plane to resync the packager (best-effort).
      void resyncChannel(channelUuid).catch(() => {});
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteSchedule(
  serviceUuid: string,
  channelUuid: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const res = await fastChannelApi.delete(`/schedules/${scheduleId}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.schedules(serviceUuid, channelUuid) });
      // Tell the control plane to resync the packager (best-effort).
      void resyncChannel(channelUuid).catch(() => {});
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Reorder a channel's playlist. The control plane persists the new order and
 * returns the reordered schedule list (normalized to the UI shape). Used by the
 * playlist-mode editor (loop/once/shuffle) where playout follows order, not
 * absolute start times.
 *
 * FROZEN endpoint: `POST /channels/{channelUuid}/schedule/reorder`,
 * body `{ ordered_ids: string[] }`.
 */
export function useReorderSchedule(
  serviceUuid: string,
  channelUuid: string,
  options?: UseMutationOptions<Schedule[], unknown, string[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const res = await fastChannelApi.post<RawSchedule[]>(
        `/channels/${channelUuid}/schedule/reorder`,
        { ordered_ids: orderedIds },
      );
      return (res.data ?? []).map(normalizeSchedule);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.schedules(serviceUuid, channelUuid) });
      // Tell the control plane to resync the packager (best-effort).
      void resyncChannel(channelUuid).catch(() => {});
      options?.onSuccess?.(...args);
    },
    onError: (...args) => {
      // A failed reorder (400 set-mismatch / epg-reject, 5xx) leaves an optimistic
      // local order out of sync with the server — refetch to restore the
      // authoritative order so the UI can't drift silently.
      qc.invalidateQueries({ queryKey: fastChannelKeys.schedules(serviceUuid, channelUuid) });
      options?.onError?.(...args);
    },
  });
}

/** Ask the control plane to tell the packager to re-fetch this channel's
 *  schedule now (so a just-saved edit plays out without the poll-TTL wait).
 *  Best-effort — failures are swallowed by callers; auto-refresh still applies. */
export async function resyncChannel(channelUuid: string): Promise<void> {
  await fastChannelApi.post(`/channels/${channelUuid}/resync`);
}

/** Re-sync playout (NON-destructive): tells the packager to re-fetch the current
 *  schedule immediately. Does NOT delete anything — unlike useFlushChannel. */
export function useResyncChannel(
  _serviceUuid: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  return useMutation({
    mutationFn: async (channelUuid: string) => {
      const res = await fastChannelApi.post(`/channels/${channelUuid}/resync`);
      return res.data;
    },
    ...options,
  });
}

/** Resolve a media source's duration (seconds) via the controlplane ffprobe probe. */
export async function fetchScheduleDuration(url: string): Promise<number | null> {
  const res = await fastChannelApi.post<{ duration?: number | null }>(
    '/storages/duration-probe',
    { url },
  );
  return res.data?.duration ?? null;
}

// ============================================
// Storages  (controlplane: read-only list + S3 file browser)
// ============================================

export function useStorages(serviceUuid: string | undefined) {
  return useQuery<Storage[]>({
    queryKey: fastChannelKeys.storages(serviceUuid ?? ''),
    enabled: !!serviceUuid,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fastChannelApi.get<Storage[]>('/storages');
      return res.data ?? [];
    },
  });
}

export function useCreateStorage(
  serviceUuid: string,
  options?: UseMutationOptions<Storage, unknown, StorageInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StorageInput) => {
      const res = await fastChannelApi.post<Storage>('/storages', input);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.storages(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

export interface UpdateStorageInput {
  storageId: string;
  input: Partial<StorageInput>;
}

export function useUpdateStorage(
  serviceUuid: string,
  options?: UseMutationOptions<Storage, unknown, UpdateStorageInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ storageId, input }: UpdateStorageInput) => {
      const res = await fastChannelApi.patch<Storage>(`/storages/${storageId}`, input);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.storages(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteStorage(
  serviceUuid: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (storageId: string) => {
      const res = await fastChannelApi.delete(`/storages/${storageId}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.storages(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useStorageFiles(
  serviceUuid: string | undefined,
  storageUuid: string | undefined,
  path: string,
  marker: string,
) {
  return useQuery<StorageFilesResult>({
    queryKey: fastChannelKeys.storageFiles(serviceUuid ?? '', storageUuid ?? '', path, marker),
    enabled: !!serviceUuid && !!storageUuid,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fastChannelApi.get<StorageFilesResult>(
        `/storages/${storageUuid}/files`,
        { params: { size: 100, path, marker } },
      );
      const d = res.data;
      return {
        common_prefixes: d?.common_prefixes ?? [],
        contents: d?.contents ?? [],
        is_truncated: d?.is_truncated,
        marker: d?.marker,
        next_marker: d?.next_marker,
      };
    },
  });
}

// ============================================
// Content Library / Assets  (controlplane: /assets + presigned upload)
// ============================================

export function useAssets(serviceUuid: string | undefined) {
  return useQuery<Asset[]>({
    queryKey: fastChannelKeys.assets(serviceUuid ?? ''),
    enabled: !!serviceUuid,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // Poll while any asset is still being normalized so the list reflects the
    // pending → normalizing → ready transition without a manual refresh; stop
    // polling once everything has settled (ready/failed).
    refetchInterval: (query) =>
      (query.state.data ?? []).some((a) => a.status === 'pending' || a.status === 'normalizing')
        ? 5_000
        : false,
    queryFn: async () => {
      const res = await fastChannelApi.get<Asset[]>('/assets');
      return res.data ?? [];
    },
  });
}

export function useCreateAsset(
  serviceUuid: string,
  options?: UseMutationOptions<Asset, unknown, CreateAssetInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAssetInput) => {
      const res = await fastChannelApi.post<Asset>('/assets', input);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.assets(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

export interface UpdateAssetInput {
  assetId: string;
  name: string;
}

export function useUpdateAsset(
  serviceUuid: string,
  options?: UseMutationOptions<Asset, unknown, UpdateAssetInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ assetId, name }: UpdateAssetInput) => {
      const res = await fastChannelApi.patch<Asset>(`/assets/${assetId}`, { name });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.assets(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Requeue a failed asset for normalization (POST /assets/{id}/retry). */
export function useRetryAsset(
  serviceUuid: string,
  options?: UseMutationOptions<Asset, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assetId: string) => {
      const res = await fastChannelApi.post<Asset>(`/assets/${assetId}/retry`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.assets(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteAsset(
  serviceUuid: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assetId: string) => {
      const res = await fastChannelApi.delete(`/assets/${assetId}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: fastChannelKeys.assets(serviceUuid) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Request a presigned PUT URL for uploading a file into a storage. The actual
 *  upload is a direct browser→storage PUT — see `uploadFileToStorage`. */
export function useUploadUrl(
  options?: UseMutationOptions<UploadUrl, unknown, UploadUrlInput>,
) {
  return useMutation({
    mutationFn: async ({ storageId, object_key, content_type }: UploadUrlInput) => {
      const res = await fastChannelApi.post<UploadUrl>(
        `/storages/${storageId}/upload-url`,
        { object_key, content_type },
      );
      return res.data;
    },
    ...options,
  });
}

/**
 * Upload a file straight to the storage using a presigned descriptor. This is
 * the ONE call that does NOT go through the control plane — it's a direct PUT to
 * the storage (S3/MinIO) the presign points at. Throws on a non-2xx response so
 * callers surface the failure instead of silently registering a missing object.
 *
 * Uses XMLHttpRequest (not fetch) because only XHR exposes upload progress
 * events — large files (hundreds of MB) need a visible progress indicator so the
 * user knows the upload is moving and when it completes.
 */
export function uploadFileToStorage(
  presign: UploadUrl,
  file: File,
  onProgress?: (loadedBytes: number, totalBytes: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(presign.method, presign.url, true);
    for (const [key, value] of Object.entries(presign.headers ?? {})) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded, e.total);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status} ${xhr.statusText || ''})`.trim()));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed (network error)')));
    xhr.addEventListener('timeout', () => reject(new Error('Upload failed (timed out)')));
    xhr.send(file);
  });
}

// ============================================
// Packager cache / PVC status
// (controlplane: /packagers/cache — fans out to each packager's /stats)
// ============================================

/** Filesystem usage of the PVC a packager's caches live on. */
export interface PackagerDisk {
  total_bytes: number;
  used_bytes: number;
  free_bytes: number;
  used_pct: number;
}

/** One channel's transmux footprint on a packager. */
export interface PackagerCacheChannel {
  channel_id: string;
  bytes: number;
  files: number;
}

/** A packager's self-reported disk + cache stats (from its /stats). */
export interface PackagerStats {
  store_dir: string;
  disk?: PackagerDisk;
  srccache: { bytes: number; files: number };
  channels: PackagerCacheChannel[];
}

/** One packager pod in the cache report. `reachable=false` carries `error`. */
export interface PackagerCache {
  id: string;
  base_url: string;
  reachable: boolean;
  error?: string;
  stats?: PackagerStats;
}

/** Pool-wide cache totals across reachable packagers. */
export interface CacheTotals {
  packagers: number;
  reachable: number;
  disk_total_bytes: number;
  disk_used_bytes: number;
  disk_free_bytes: number;
  srccache_bytes: number;
  srccache_files: number;
}

export interface CacheStatusResponse {
  packagers: PackagerCache[];
  totals: CacheTotals;
}

const EMPTY_CACHE_TOTALS: CacheTotals = {
  packagers: 0,
  reachable: 0,
  disk_total_bytes: 0,
  disk_used_bytes: 0,
  disk_free_bytes: 0,
  srccache_bytes: 0,
  srccache_files: 0,
};

/**
 * Live cache/PVC status across the packager pool. Polled on an interval since
 * the on-disk caches grow without eviction — this is the headroom gauge. Tenant
 * is resolved from the JWT, so the report is cluster-wide (not service-scoped).
 */
export function usePackagerCache() {
  return useQuery<CacheStatusResponse>({
    queryKey: fastChannelKeys.cache,
    staleTime: 10_000,
    refetchInterval: 15_000,
    queryFn: async () => {
      const res = await fastChannelApi.get<CacheStatusResponse>('/packagers/cache');
      return {
        packagers: res.data?.packagers ?? [],
        totals: res.data?.totals ?? EMPTY_CACHE_TOTALS,
      };
    },
  });
}

// ============================================
// Channel analytics (controlplane: /channels/{id}/analytics — live snapshot)
// ============================================

/** Per-channel delivery metrics from the owning packager. CCV is a server-side
 *  approximation (playlist-poll window), not a billing-grade beacon count. */
export interface ChannelMetrics {
  channel_id: string;
  active_viewers: number;
  playlist_requests: number;
  segment_requests: number;
  bytes_served: number;
  cache_hits: number;
  cache_misses: number;
  cache_hit_ratio: number; // 0..1
  viewer_window_sec: number;
  segments_produced: number;
  last_segment_age_sec: number; // -1 if none yet; high → production stalled
  /** Resource footprint of the packager pod this channel runs on (pod-wide). */
  pod?: {
    goroutines: number;
    heap_bytes: number;
    num_cpu: number;
    cpu_seconds: number;
    uptime_seconds: number;
    channels_on_pod: number;
    transmux_jobs: number;
    transmux_seconds: number;
  };
}

/** Live channel delivery metrics, polled on an interval. */
export function useChannelAnalytics(channelUuid: string | undefined) {
  return useQuery<ChannelMetrics | null>({
    queryKey: fastChannelKeys.analytics(channelUuid ?? ''),
    enabled: !!channelUuid,
    staleTime: 5_000,
    refetchInterval: 10_000,
    queryFn: async () => {
      const res = await fastChannelApi.get<ChannelMetrics>(`/channels/${channelUuid}/analytics`);
      return res.data ?? null;
    },
  });
}
